(() => {
  if (customElements.get('tt-collection-pagination')) return;
  class CollectionPagination extends HTMLElement {
    connectedCallback() {
      this.grid = this.closest('#ProductGridContainer');
      this.list = this.grid?.querySelector('#ProductsList');
      this.button = this.querySelector('button');
      this.label = this.querySelector('.tt-browse-label');
      this.status = this.querySelector('.tt-browse-status');
      this.progress = this.querySelector('.tt-browse-progress');
      if (!this.list || !this.button) return;
      this.originalLabel = this.label.textContent;
      this.base = new URL(location.href);
      this.base.searchParams.delete('section_id');
      this.base.searchParams.delete('page');
      this.base.searchParams.sort();
      this.startPage = Number(this.dataset.page);
      this.pages = [this.startPage];
      this.key = 'tt-collection-return-v1:' + this.base.href;
      this.onClick = (event) => this.loadNext({ keyboard: event.detail === 0 });
      this.remember = (event) => {
        if (event.type === 'click' && !event.target.closest('#ProductsList a[href*="/products/"]')) return;
        this.save();
      };
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('click', this.remember);
      window.addEventListener('pagehide', this.remember);
      this.querySelector('.tt-browse-pages')?.removeAttribute('open');
      this.button.hidden = !this.dataset.next;
      this.updateProgress();
      this.restore().finally(() => this.observeNext());
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.prefetchObserver?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.remember);
      window.removeEventListener('pagehide', this.remember);
    }
    observeNext() {
      this.prefetchObserver?.disconnect();
      if (!this.isConnected || !this.dataset.next || !('IntersectionObserver' in window)) return;
      const connection = navigator.connection;
      if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
      this.prefetchObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.prefetchNext();
      }, { rootMargin: '600px 0px' });
      this.prefetchObserver.observe(this.button);
    }
    prefetchNext() {
      if (this.busy || this.restoring || !this.dataset.next || this.prefetched) return;
      let url;
      try { url = this.safeUrl(this.dataset.next); } catch (_) { return; }
      const pending = { url: url.href, time: Date.now() };
      this.prefetched = pending;
      this.dataset.prefetch = 'loading';
      pending.promise = this.requestPage(url).then(html => {
        if (this.isConnected && this.prefetched === pending) this.dataset.prefetch = 'ready';
        return html;
      }).catch(() => {
        if (this.prefetched === pending) {
          this.prefetched = null;
          delete this.dataset.prefetch;
        }
        return null;
      });
    }
    format(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => text.replaceAll('[' + key + ']', String(value)), template);
    }
    updateProgress() {
      const count = this.list.querySelectorAll('.product-column').length;
      const total = Number(this.dataset.total);
      this.progress.textContent = this.format(this.dataset.progress, {
        first: total ? Number(this.dataset.offset) + 1 : 0,
        shown: Math.min(Number(this.dataset.offset) + count, total), total
      });
    }
    safeUrl(path) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin || url.pathname !== location.pathname) throw new Error('Invalid collection URL');
      for (const [key, value] of this.base.searchParams) if (!url.searchParams.has(key)) url.searchParams.append(key, value);
      url.searchParams.set('section_id', this.dataset.section);
      return url;
    }
    async requestPage(url) {
      this.controller = new AbortController();
      const timer = setTimeout(() => this.controller.abort(), 20000);
      try {
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } finally {
        clearTimeout(timer);
      }
    }
    async loadNext({ keyboard = false, restoring = false } = {}) {
      if (this.busy || (this.restoring && !restoring) || !this.dataset.next) return false;
      this.busy = true;
      this.dataset.busy = 'true';
      this.button.setAttribute('aria-disabled', 'true');
      this.label.textContent = this.dataset.loading;
      this.status.textContent = this.dataset.loading;
      this.list.setAttribute('aria-busy', 'true');
      try {
        const url = this.safeUrl(this.dataset.next);
        const cached = !restoring && this.prefetched?.url === url.href && Date.now() - this.prefetched.time < 60000 ? this.prefetched : null;
        const html = (cached && await cached.promise) || await this.requestPage(url);
        this.prefetched = null;
        delete this.dataset.prefetch;
        const source = html.querySelector('#ProductsList');
        const pager = html.querySelector('tt-collection-pagination');
        if (!source || !pager || Number(pager.dataset.page) !== this.pages.at(-1) + 1 || pager.dataset.total !== this.dataset.total) throw new Error('Unexpected collection response');
        const existing = new Set([...this.list.querySelectorAll('[data-tt-product-id]')].map(item => item.dataset.ttProductId));
        const cards = [...source.querySelectorAll('.product-column')].filter(item => !existing.has(item.dataset.ttProductId));
        if (!cards.length) throw new Error('No new products');
        const fragment = document.createDocumentFragment();
        cards.forEach(card => {
          card.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; });
          fragment.append(card);
        });
        this.list.append(fragment);
        this.dataset.next = pager.dataset.next;
        this.pages.push(Number(pager.dataset.page));
        this.dataset.page = pager.dataset.page;
        const fallback = this.querySelector('.tt-browse-pages');
        const newFallback = pager.querySelector('.tt-browse-pages');
        if (fallback && newFallback) fallback.innerHTML = newFallback.innerHTML;
        this.updateProgress();
        this.status.textContent = this.format(this.dataset.complete, { added: cards.length }) + ' ' + this.progress.textContent;
        this.button.hidden = !this.dataset.next;
        const nextLocation = new URL(location.href);
        nextLocation.searchParams.set('page', this.dataset.page);
        if (!restoring) history.replaceState(history.state, '', nextLocation);
        if (keyboard) cards[0].querySelector('a[href*="/products/"]')?.focus({ preventScroll: true });
        if (!restoring) this.save();
        return true;
      } catch (error) {
        if (this.isConnected) {
          this.status.textContent = this.dataset.error;
          this.querySelector('.tt-browse-pages')?.setAttribute('open', '');
        }
        return false;
      } finally {
        if (this.isConnected) {
          this.busy = false;
          delete this.dataset.busy;
          this.list.removeAttribute('aria-busy');
          this.button.removeAttribute('aria-disabled');
          this.label.textContent = this.originalLabel;
          if (!restoring) this.observeNext();
        }
      }
    }
    save() {
      if (this.busy) return;
      try {
        const anchor = [...this.list.querySelectorAll('.product-column')].find(card => card.getBoundingClientRect().bottom > 150);
        sessionStorage.setItem(this.key, JSON.stringify({
          base: this.base.href, start: this.startPage, pages: this.pages, page: Number(this.dataset.page),
          y: scrollY, anchor: anchor?.dataset.ttProductId, top: anchor?.getBoundingClientRect().top,
          filters: [...document.querySelectorAll('#FacetFiltersContainer details[open][data-index]')].map(item => item.dataset.index),
          time: Date.now()
        }));
      } catch (_) {}
    }
    async restore() {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation?.type !== 'back_forward' && navigation?.type !== 'reload') return;
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem(this.key)); } catch (_) { return; }
      if (!saved || saved.base !== this.base.href || Date.now() - saved.time > 1800000 || saved.page !== Number(this.dataset.page)) return;
      if (!Array.isArray(saved.pages) || saved.pages.length > 100 || saved.start < 1) return;
      this.restoring = true;
      this.button.setAttribute('aria-disabled', 'true');
      try {
        if (saved.start !== this.startPage) {
          this.dataset.busy = 'true';
          this.status.textContent = this.dataset.loading;
          const firstUrl = new URL(this.base);
          firstUrl.searchParams.set('page', saved.start);
          const html = await this.requestPage(this.safeUrl(firstUrl));
          const source = html.querySelector('#ProductsList');
          const pager = html.querySelector('tt-collection-pagination');
          if (!source || !pager || pager.dataset.total !== this.dataset.total) throw new Error('Restore response changed');
          this.list.replaceChildren(...source.children);
          this.dataset.offset = pager.dataset.offset;
          this.dataset.next = pager.dataset.next;
          this.dataset.page = pager.dataset.page;
          this.startPage = saved.start;
          this.pages = [saved.start];
          delete this.dataset.busy;
        }
        while (this.pages.length < saved.pages.length && this.isConnected) {
          if (!await this.loadNext({ restoring: true })) throw new Error('Restore incomplete');
        }
        this.updateProgress();
        this.button.hidden = !this.dataset.next;
        document.querySelectorAll('#FacetFiltersContainer details[data-index]').forEach(detail => { detail.open = saved.filters?.includes(detail.dataset.index); });
        const anchor = [...this.list.querySelectorAll('.product-column')].find(card => card.dataset.ttProductId === saved.anchor);
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          window.scrollTo({ top: anchor ? scrollY + anchor.getBoundingClientRect().top - saved.top : saved.y, behavior: 'instant' });
        });
      } catch (error) {
        if (this.isConnected) {
          this.status.textContent = this.dataset.error;
          this.querySelector('.tt-browse-pages')?.setAttribute('open', '');
        }
      } finally {
        this.restoring = false;
        this.button.removeAttribute('aria-disabled');
        delete this.dataset.busy;
      }
    }
  }
  customElements.define('tt-collection-pagination', CollectionPagination);
})();
